import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { SocoAiLogo } from "@/components/Holo";
import { Button, Dialog, Label, Switch, Textarea } from "@/components/ui";
import { CoachOption } from "@/containers/Coach/PromptStudio/components/CoachPicker";
import { GENERATE_FAILED } from "@/containers/Coach/PromptStudio/hooks/useBackgroundMutations";
import { BackgroundAssetServices } from "@/services/bedrock/BackgroundAssetServices";
import { GenerateBackgroundsRequest } from "@/services/bedrock/dtos/BackgroundAsset";
import { cn } from "@/lib/utils";
import { designKeys } from "@/services/bedrock/queryKeys";
import { LibraryMode, LibraryScope, PREVIEW_RATIO_ITEMS, PreviewRatio } from "./LibraryToolbar";

type AdminScope = Exclude<LibraryScope, "all">;

export interface LibraryGenerateDialogProps {
  mode: LibraryMode;
  open: boolean;
  onOpenChange(open: boolean): void;
  /** Admin mode: the toolbar's coach, used when generating into a coach library. */
  coach?: CoachOption | null;
  /** Admin mode: the library the new background goes into, set by the page's Global | Coach scope. */
  scope?: AdminScope;
  /** Prefilled from an asset's prompt ("New … from this prompt"). */
  initialPrompt?: string;
}

// Each frame is 24px tall so the shapes compare at a glance.
const FRAME_WIDTH: Record<PreviewRatio, string> = {
  square_1_1: "w-6",
  portrait_4_5: "w-[19px]",
  portrait_9_16: "w-[14px]",
};
const TILE_IDLE = "border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:bg-slate-50";
const TILE_ACTIVE = "border-slate-400 bg-slate-100 text-slate-900";

/**
 * The size the new background is made at, as picked frames like the library's filters; the same
 * control in both modes. The other sizes are made from it afterwards.
 */
const SizeField: React.FC<{ value: PreviewRatio; onChange(value: PreviewRatio): void }> = ({ value, onChange }) => {
  const refs = React.useRef<(HTMLButtonElement | null)[]>([]);
  // Arrow keys move the choice, as in any radio group.
  const onKeyDown = (event: React.KeyboardEvent, index: number) => {
    const step =
      event.key === "ArrowRight" || event.key === "ArrowDown"
        ? 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
        ? -1
        : 0;
    if (!step) return;
    event.preventDefault();
    const next = (index + step + PREVIEW_RATIO_ITEMS.length) % PREVIEW_RATIO_ITEMS.length;
    onChange(PREVIEW_RATIO_ITEMS[next].value);
    refs.current[next]?.focus();
  };

  return (
    <div className="flex flex-col gap-1.5">
      <span id="library-generate-size" className="text-sm font-medium text-slate-700">
        Main size
      </span>
      <div className="flex gap-2" role="radiogroup" aria-labelledby="library-generate-size">
        {PREVIEW_RATIO_ITEMS.map((item, index) => {
          const selected = item.value === value;
          return (
            <button
              key={item.value}
              ref={el => (refs.current[index] = el)}
              type="button"
              role="radio"
              aria-checked={selected}
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(item.value)}
              onKeyDown={event => onKeyDown(event, index)}
              className={cn(
                "flex h-16 w-16 flex-col items-center justify-center gap-1.5 rounded-lg border transition-colors cursor-pointer",
                selected ? TILE_ACTIVE : TILE_IDLE
              )}
            >
              <span
                aria-hidden="true"
                className={cn("block h-6 rounded-[3px] border-[1.5px] border-current", FRAME_WIDTH[item.value])}
              />
              <span className="text-xs font-semibold">{item.label}</span>
            </button>
          );
        })}
      </div>
      <p className="text-xs text-slate-500">We'll make the other sizes from it, too.</p>
    </div>
  );
};
/** "Create with [SocialCoach AI]"; screen readers hear the logo's name. */
const CreateWithTitle: React.FC = () => (
  <Dialog.Title className="flex items-center gap-2">
    Create with
    <SocoAiLogo variant="color" height={18} className="translate-y-px" />
  </Dialog.Title>
);

/** Where the new background lands, said once under the title. */
function destination(mode: LibraryMode, scope: AdminScope, coach: CoachOption | null): string {
  if (mode === "coach") return "Describe the scene. New backgrounds join your library and work on any design.";
  if (scope === "global") return "Adds a draft to the global library.";
  return coach ? `Adds to ${coach.label}'s library.` : "Pick a coach first.";
}

/**
 * Create with AI, one background at a time, for coaches and admins. A coach's lands in their library,
 * active, at editor priority; an admin's goes where the page's scope is (a global draft, or a coach's
 * library), at low priority when asked.
 */
export const LibraryGenerateDialog: React.FC<LibraryGenerateDialogProps> = ({
  mode,
  open,
  onOpenChange,
  coach = null,
  scope = "global",
  initialPrompt = "",
}) => {
  const queryClient = useQueryClient();
  const isAdmin = mode === "admin";
  const [prompt, setPrompt] = React.useState(initialPrompt);
  const [ratio, setRatio] = React.useState<PreviewRatio>("portrait_4_5");
  const [bulk, setBulk] = React.useState(scope === "global");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setPrompt(initialPrompt);
    setRatio("portrait_4_5");
    setBulk(scope === "global");
  }, [open, initialPrompt, scope]);

  const needsCoach = isAdmin && scope === "coach" && coach === null;
  const canSubmit = prompt.trim().length > 0 && !needsCoach && !busy;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    // Coaches send no scope, visibility, or priority: the server decides all three for them.
    const request: GenerateBackgroundsRequest = isAdmin
      ? {
          instruction: prompt.trim(),
          count: 1,
          ratio,
          scope,
          visibility: scope === "global" ? "draft" : "active",
          bulk,
          ...(scope === "coach" && coach ? { coachId: coach.id } : {}),
        }
      : { instruction: prompt.trim(), count: 1, ratio };
    setBusy(true);
    try {
      await BackgroundAssetServices.generate(request);
      toast.success("Creating a background…");
      queryClient.invalidateQueries({ queryKey: designKeys.backgrounds.requests({}).slice(0, 1) });
      if (isAdmin) queryClient.invalidateQueries({ queryKey: designKeys.backgrounds.queue });
      onOpenChange(false);
    } catch {
      toast.error(GENERATE_FAILED);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={next => onOpenChange(next)} size="md">
      <Dialog.Header>
        <CreateWithTitle />
        <Dialog.Description>{destination(mode, scope, coach)}</Dialog.Description>
      </Dialog.Header>
      <form id="library-generate-backgrounds" onSubmit={submit}>
        <Dialog.Content className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="library-generate-prompt">Prompt</Label>
            <Textarea
              id="library-generate-prompt"
              value={prompt}
              onChange={event => setPrompt(event.target.value)}
              placeholder="e.g. snowy cabin exterior at dusk, empty sky at the top"
              rows={4}
              disabled={busy}
            />
          </div>
          <SizeField value={ratio} onChange={setRatio} />
          {isAdmin && (
            <label className="flex items-center gap-3 cursor-pointer text-sm text-slate-700">
              <Switch checked={bulk} onCheckedChange={setBulk} aria-label="Low priority (run when the queue is free)" />
              Low priority (run when the queue is free)
            </label>
          )}
        </Dialog.Content>
      </form>
      <Dialog.Footer>
        <Button type="submit" form="library-generate-backgrounds" loading={busy} disabled={!canSubmit}>
          Create
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
};
